/**
 * apiMessage.js — traduce las respuestas del API legacy (`/api/v1/auth/*`,
 * `/api/v1/products/*`, `/api/v1/qr/*`, ranking…) a un mensaje localizado.
 *
 * Gemelo de `gbusinessErrors.js`, pero para las views de la app `api/` de
 * Django, que devuelven `{ error | message, code }`. Usamos el `code` estable
 * (snake_case) para buscar la clave i18n y, si no lo reconocemos, caemos al
 * texto crudo del backend (español) y por último a un genérico. Así el mismo
 * endpoint muestra el texto en el idioma del usuario sin depender del literal
 * que mande el servidor.
 *
 * El backend emite dos familias de `code`:
 *   - errores  → clave `api.errors.<code>`
 *   - éxitos   → clave `api.messages.<code>`
 * Como el conjunto de codes no se solapa entre error/éxito, probamos ambas
 * ramas y usamos la que exista.
 *
 * Uso:
 *   import { apiMessage } from '@/api/apiMessage';
 *   // error de un catch:
 *   toast.error(apiMessage(error, t));
 *   // éxito (data de una respuesta 2xx):
 *   toast.success(apiMessage(data, t, t('algún.fallback')));
 */

/**
 * Extrae `{ code, error, message }` tanto de un AxiosError (`error.response.data`)
 * como de un payload de respuesta directo (`{ code, message }`).
 */
function extract(source) {
  const data = source?.response?.data ?? source ?? {};
  return {
    code: typeof data.code === 'string' ? data.code : null,
    // El backend usa `error` para fallos y `message` para éxitos; cualquiera
    // de los dos sirve de fallback textual.
    raw: (typeof data.error === 'string' && data.error)
      || (typeof data.message === 'string' && data.message)
      || null,
  };
}

/**
 * @param {any} source  AxiosError (catch) o payload de respuesta (data).
 * @param {(key: string, opts?: any) => string} t  i18next `t`.
 * @param {string} [fallback]  Mensaje si no hay code conocido ni texto del backend.
 * @returns {string}
 */
export function apiMessage(source, t, fallback) {
  const { code, raw } = extract(source);

  if (code) {
    for (const key of [`api.errors.${code}`, `api.messages.${code}`]) {
      const translated = t(key);
      if (translated !== key) return translated;
    }
  }

  // Sin code reconocido: preferimos el fallback i18n del llamador (traducido);
  // si no lo hay, el texto del backend (español) es mejor que un genérico vacío.
  return fallback || raw || t('api.errors.generic');
}
